/* eslint-disable max-lines */
import type {SharedData} from '@gaia/types';
import getMapPaddingWithSidebar from '@gaia/utils/gaia/getMapPaddingWithSidebar';
import getOSMFeatureName from '@gaia/utils/gaia/getOSMFeatureName';
import {
    areaFixedLayerIds,
    trackFixedLayerIds,
    waypointFixedLayerIds,
} from '@gaia/utils/gaia/getSyncMapboxStyle/fixedLayerIds';
import isFeatureType from '@gaia/utils/gaia/isFeatureType';
import isPointBehindSidebar from '@gaia/utils/gaia/isPointBehindSidebar';
import selectOSMFeature from '@gaia/utils/gaia/selectOSMFeature';
import smartSetSelections from '@gaia/utils/gaia/smartSetSelections';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import type {
    MapCursor,
    UseInteractionHandlersOptions,
} from '@gaia/utils/gaia/useInteractionHandlers';
import find from 'lodash/find';
import flatten from 'lodash/flatten';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import partition from 'lodash/partition';
import throttle from 'lodash/throttle';
import uniqBy from 'lodash/uniqBy';

const fixedUserLayerIds = [
    ...flatten(Object.values(waypointFixedLayerIds).map((d) => Object.values(d))),
    ...flatten(Object.values(trackFixedLayerIds).map((d) => Object.values(d))),
    ...flatten(Object.values(areaFixedLayerIds).map((d) => Object.values(d))),
];

function onInteractionChangeSafe(
    interaction: InteractionState,
    nextInteraction: InteractionState,
    onInteractionChange: React.Dispatch<React.SetStateAction<InteractionState>>
) {
    if (!isEqual(interaction, nextInteraction)) {
        onInteractionChange(nextInteraction);
    }
}

function getInteractiveFeatures(
    features: mapboxgl.MapboxGeoJSONFeature[],
    mapboxStyle: GaiaMapboxStyle | undefined
) {
    const clickableFeatures = features.filter((f) =>
        find(isFeatureType, (is) => is(f, mapboxStyle))
    );
    return clickableFeatures;
}

function panMapForSidebar(
    map: mapboxgl.Map | undefined,
    lngLat: mapboxgl.LngLat,
    controlsExpanded: boolean | undefined
) {
    if (!map) return;
    // If point will be covered by sidebar, shift the map right to expose it
    if (isPointBehindSidebar(map, lngLat, controlsExpanded)) {
        const markerOffset = 16;
        const padding = getMapPaddingWithSidebar(controlsExpanded).left + markerOffset;
        const pointInPx = map.project(lngLat);
        map.panBy([pointInPx.x - padding, 0]);
    }
}

export const normal = {
    getCursor: (interaction: NormalInteractionState): MapCursor => {
        if (interaction.subtype === 'hovering') {
            return 'pointer';
        } else {
            return 'default';
        }
    },

    onMouseDown: () => {},

    onMouseUp: () => {},

    onMouseMove: throttle(
        (
            event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
            {
                onInteractionChange,
                mapboxStyle,
                sidebar,
                interaction,
                importState,
            }: UseInteractionHandlersOptions<NormalInteractionState>
        ) => {
            const bboxOffset = 5;
            let queriedFeatures = event.target
                .queryRenderedFeatures([
                    [event.point.x - bboxOffset, event.point.y + bboxOffset],
                    [event.point.x + bboxOffset, event.point.y - bboxOffset],
                ])
                .map((f: any) => f.toJSON());

            const parkLabelFeature = queriedFeatures.find((f) => f.layer.id.includes('park-label'));
            const userDataFeature = queriedFeatures.find((f) =>
                fixedUserLayerIds.includes(f.layer.id)
            );

            // Enabling hover on park labels requires merging properties from two layers
            if (parkLabelFeature && !userDataFeature) {
                // Query land features with a larger bbox
                const landBboxOffset = 50;
                const parkFillFeature = event.target
                    .queryRenderedFeatures([
                        [event.point.x + landBboxOffset, event.point.y + landBboxOffset],
                        [event.point.x - landBboxOffset, event.point.y - landBboxOffset],
                    ])
                    .map((f: any) => f.toJSON())
                    .find((qf) => {
                        return (
                            qf.layer.id.includes('park-fill') &&
                            qf.properties.name === parkLabelFeature.properties.name
                        );
                    });

                if (parkFillFeature) {
                    queriedFeatures = [
                        {
                            ...parkLabelFeature,
                            properties: {
                                ...parkLabelFeature.properties,
                                ...parkFillFeature.properties,
                                type: 'land',
                            },
                        },
                        ...queriedFeatures.slice(1),
                    ];
                }
            }

            const features = getInteractiveFeatures(queriedFeatures, mapboxStyle);

            // Prioritize user data in interactions
            const feature =
                features.find((f) => fixedUserLayerIds.includes(f.layer.id)) || features[0];

            const {markedLocation} = interaction;

            const isImportedFeature =
                importState.status !== 'idle' && feature?.properties?.isLatestImport;

            if (feature && importState.status !== 'idle' && isImportedFeature) {
                onInteractionChangeSafe(
                    interaction,
                    {
                        type: 'normal',
                        subtype: 'hovering',
                        feature,
                        markedLocation,
                    },
                    onInteractionChange
                );
            } else if (feature && importState.status === 'idle' && sidebar.type !== 'print') {
                onInteractionChangeSafe(
                    interaction,
                    {
                        type: 'normal',
                        subtype: 'hovering',
                        feature,
                        markedLocation,
                    },
                    onInteractionChange
                );
            } else {
                onInteractionChangeSafe(
                    interaction,
                    {type: 'normal', subtype: 'normal', markedLocation},
                    onInteractionChange
                );
            }
        },
        200,
        {leading: true, trailing: true}
    ),

    onMouseOut: () => {},

    onClick: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            interaction,
            setSelections,
            waypointDispatcher,
            trackDispatcher,
            areaDispatcher,
            sharedData,
            mapboxStyle,
            selections,
            map,
            importState,
            setImportState,
            onSidebarChange,
            querySourceFeatures,
            isEmbedded,
            onInteractionChange,
            sidebar,
            controlsExpanded: controlsExpanded,
            unitSystem,
        }: UseInteractionHandlersOptions<NormalInteractionState>
    ) => {
        const folder =
            interaction.subtype === 'hovering' && getFolderId(interaction.feature, sharedData);

        const feature: mapboxgl.MapboxGeoJSONFeature | undefined =
            interaction.subtype === 'hovering'
                ? {
                      ...interaction.feature,
                      properties: {
                          ...interaction.feature.properties,
                          ...(folder ? {folder} : undefined),
                      },
                  }
                : undefined;

        const mapboxLayerId = feature?.layer.id;
        const {lngLat} = event;

        const queriedFeatures = event.target
            .queryRenderedFeatures([
                [event.point.x - 5, event.point.y + 5],
                [event.point.x + 5, event.point.y - 5],
            ])
            .map((f: any) => f.toJSON());

        let nextMarkedLocation: mapboxgl.LngLat | undefined = lngLat;

        const interactiveFeatures = getInteractiveFeatures(queriedFeatures, mapboxStyle);

        // Some Polygon and MultiPolygon interaction layers (e.g. Cell Coverage - All, Native Lands) do not have an id,
        // and return multiple layers for the same source. These must be excluded from deduping.
        const [interactionLayers, features] = partition(
            interactiveFeatures,
            (f) => isFeatureType.interactionSpec(f, mapboxStyle) && !f.id
        );

        // Features on tile boundaries often return multiple partial geometries for
        // a single ID. These must be deduped in order to return an accurate feature count.
        const allFeatures = uniqBy(features, (f) =>
            isFeatureType.osm(f) ? f.properties?.osm_id : f.id || f.properties?.id
        ).concat(interactionLayers);

        // Prioritize selecting routes, tracks, or waypoints if only one exists in the result set. This avoids
        // the case where a waypoint or track may overlay a feature that is not visible (such as a trail)
        // and interfere with the user's ability to select their data.
        const userFeatures = allFeatures.filter(
            (f) => isFeatureType.waypoint(f) || isFeatureType.track(f) || isFeatureType.area(f)
        );
        const isAreaOverlay = feature && isFeatureType.area(feature) && allFeatures.length > 1;

        const isPOIcluster = feature && isFeatureType.poiCluster(feature);

        const isParkLabel = feature && feature.layer.id.includes('park-label');

        const isSingleSelection =
            allFeatures.length === 1 || (userFeatures.length === 1 && !isAreaOverlay);

        const shouldDisambiguate =
            !isPOIcluster &&
            !isParkLabel &&
            !isSingleSelection &&
            importState.status === 'idle' &&
            !isEmbedded &&
            sidebar.type !== 'print';

        if (shouldDisambiguate) {
            setSelections([]);
            panMapForSidebar(map, lngLat, controlsExpanded);
            onSidebarChange({
                type: 'disambiguation',
                coordinates: lngLat.toArray(),
                features: allFeatures,
                mapboxStyle,
            });
            ampli.openSidebar({
                type: 'disambiguation',
            });
            ampli.clickedMap({
                type: 'disambiguation',
                layer: mapboxLayerId,
                featureCount: allFeatures.length,
            });
        } else if (!feature) {
            if (importState.status === 'selecting') {
                setImportState({...importState, popup: undefined});
            }
        } else if (map && isPOIcluster && importState.status === 'idle') {
            map.easeTo({center: event.lngLat, zoom: map.getZoom() + 2});
            ampli.clickedMap({
                title: feature?.properties?.name,
                type: 'poi cluster',
                class: feature?.properties?.type ?? '',
                layer: mapboxLayerId,
            });
            // Don't update location if it's a POI cluster
            nextMarkedLocation = interaction.markedLocation;
        } else if (isFeatureType.area(feature)) {
            ampli.openObjectDetails({
                category: 'map2',
                type: 'area',
                location: 'map',
            });
            ampli.clickedMap({
                type: 'User Object',
                userObjectType: 'area',
                layer: mapboxLayerId,
                title: feature.properties.title,
            });

            const area: Area = feature;

            const localId = areaDispatcher({
                type: 'takeLocal',
                area,
            })[0];

            if (importState.status === 'selecting') {
                setImportState({
                    ...importState,
                    popup: {type: 'area', localId},
                });
            } else {
                smartSetSelections(event.originalEvent, selections, setSelections, {
                    type: 'area',
                    localId,
                });
                onSidebarChange({
                    type: 'areaDetails',
                    id: localId,
                });
            }
        } else if (isFeatureType.track(feature)) {
            ampli.clickedMap({
                type: 'User Object',
                userObjectType: 'track',
                layer: mapboxLayerId,
                title: feature.properties.title,
            });

            const track: Track = feature;

            const localId = trackDispatcher({
                type: 'takeLocal',
                track,
            })[0];

            if (importState.status === 'selecting') {
                setImportState({
                    ...importState,
                    popup: {type: 'track', localId},
                });
            } else {
                smartSetSelections(event.originalEvent, selections, setSelections, {
                    type: 'track',
                    localId,
                });
            }

            onSidebarChange({
                type: 'trackDetails',
                id: localId,
            });
        } else if (isFeatureType.publicTrack(feature) && importState.status === 'idle') {
            ampli.clickedMap({
                type: 'Public Track',
                layer: mapboxLayerId,
                title: feature.properties.title,
            });

            const localId = trackDispatcher({
                type: 'takeLocal',
                track: {
                    properties: {
                        ...feature.properties,
                        isPublicTrack: true,
                    },
                },
            })[0];

            setSelections([
                {
                    type: 'track',
                    localId,
                },
            ]);

            onSidebarChange({
                type: 'trackDetails',
                id: localId,
            });
        } else if (isFeatureType.waypoint(feature)) {
            ampli.clickedMap({
                type: 'User Object',
                userObjectType: 'waypoint',
                layer: mapboxLayerId,
                title: feature.properties.title,
            });

            const localId = waypointDispatcher({
                type: 'takeLocal',
                waypoint: feature,
            })[0];

            if (importState.status === 'selecting') {
                setImportState({
                    ...importState,
                    popup: {type: 'waypoint', localId},
                });
            } else {
                smartSetSelections(event.originalEvent, selections, setSelections, {
                    type: 'waypoint',
                    localId,
                });
                onSidebarChange({
                    type: 'waypointDetails',
                    id: localId,
                });
            }
            // Since a waypoint already marks a single point, we should never show an additional marked location
            nextMarkedLocation = undefined;
        } else if (isFeatureType.osm(feature)) {
            ampli.clickedMap({
                title: getOSMFeatureName(feature.properties, unitSystem),
                type: 'OSM Object',
                osmType: feature?.properties?.type || '',
                class: feature?.properties?.class || '',
                layer: mapboxLayerId,
            });

            selectOSMFeature({
                feature,
                lngLat,
                mapboxLayerId,
                setSelections,
                querySourceFeatures,
                unitSystem,
            });
            onSidebarChange({type: 'selection'});
            // Don't show the marked location when selecting a POI
            if (feature.geometry.type === 'Point') {
                nextMarkedLocation = undefined;
            }
        } else if (
            isFeatureType.interactionSpec(feature, mapboxStyle) &&
            importState.status === 'idle'
        ) {
            ampli.clickedMap({
                type: 'Feature overlay',
                layer: mapboxLayerId,
            });

            onSidebarChange({type: 'interactionSpec'});

            setSelections([
                {
                    type: 'interactionSpec',
                    feature,
                },
            ]);
        } else if (isFeatureType.knownRoute(feature) && importState.status === 'idle') {
            ampli.clickedMap({
                type: 'known_route',
                id: feature.properties!.id,
                title: feature.properties!.title,
                layer: mapboxLayerId,
            });

            onSidebarChange({type: 'knownRoute'});

            setSelections([
                {
                    type: 'knownRoute',
                    knownRouteId: feature.properties!.id,
                },
            ]);
        } else if (importState.status === 'selecting') {
            setImportState({...importState, popup: undefined});
        } else {
            setSelections([]);
            onSidebarChange({type: 'hidden'});
        }

        if (
            importState.status === 'idle' &&
            (interaction.subtype === 'normal' || interaction.subtype === 'hovering') &&
            sidebar.type !== 'print'
        ) {
            onInteractionChangeSafe(
                interaction,
                {
                    ...interaction,
                    markedLocation: nextMarkedLocation,
                },
                onInteractionChange
            );
        }
    },

    onDblClick: () => {},
};

function getFolderId(feature: mapboxgl.MapboxGeoJSONFeature, sharedData: SharedData) {
    if (
        sharedData &&
        sharedData.type === 'folder' &&
        (includes(trackFixedLayerIds.remoteShared, feature.layer.id) ||
            includes(areaFixedLayerIds.remoteShared, feature.layer.id) ||
            includes(waypointFixedLayerIds.remoteShared, feature.layer.id))
    ) {
        return sharedData.id;
    } else {
        return undefined;
    }
}
